import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	input,
	signal,
} from '@angular/core';
import { RelativeTimePipe } from '@context/frontend/common';
import { AvatarComponent, UserNamePipe } from '@context/frontend/user';
import {
	Notification,
	NotificationType,
} from '@context/shared/types/notification';
import { User } from '@context/shared/types/user';
import { TranslocoModule } from '@jsverse/transloco';
import { TuiHint, TuiIcon } from '@taiga-ui/core';
import { DocumentReference, getDoc } from 'firebase/firestore';
import { QuillModule } from 'ngx-quill';

const DefaultNotificationIcon = '@tui.bell';
const NotificationIconMap: { [key in NotificationType]: string } = {
	'system-accepted-invite': '@tui.mail-check',
	'system-rejected-invite': '@tui.mail-minus',
	'user-shared-file': '@tui.share-2',
	'user-revoked-file': '@tui.user-round-x',
	'user-shared-folder': '@tui.share-2',
	'user-revoked-folder': '@tui.user-round-x',
	'user-added-log': '@tui.notebook-pen',
	'user-archived-log': '@tui.circle-minus',
};

@Component({
	imports: [
		AvatarComponent,
		RelativeTimePipe,
		QuillModule,
		TranslocoModule,
		TuiHint,
		UserNamePipe,
		TuiIcon,
	],
	selector: 'ctx-notification-row',
	templateUrl: 'notification-row.component.html',
	styleUrl: 'notification-row.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		'[attr.unread]': 'unread()',
	},
})
export class NotificationRowComponent {
	readonly notification = input.required<Notification>();

	readonly sender = signal<User | null>(null);
	readonly icon = computed<string>(() => {
		const notification = this.notification();
		const icon = NotificationIconMap[notification.type];
		if (!icon) return DefaultNotificationIcon;
		return icon;
	});

	readonly unread = computed(() => !this.notification().acknowledgedAt);

	constructor() {
		effect(() => {
			const notification = this.notification();
			const sender = this.sender();

			// already fetched
			if (sender?.id === notification.createdBy?.id) return;

			// fetch new if not populated
			if (notification.createdBy && !sender) this.fetchSender();
		});
	}

	async fetchSender() {
		const notification = this.notification();
		if (!notification.createdBy) return;

		const doc = await getDoc(
			notification.createdBy as DocumentReference<User>,
		);
		this.sender.set(doc.data() as User);
	}
}
